import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, Redirect } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import Home from "./pages/Home";
import DailyLogEntry from "./pages/DailyLogEntry";
import DailyLogEntryWrapper from "./pages/DailyLogEntryWrapper";
import DailyLogList from "./pages/DailyLogList";
import WellsManagement from "./pages/WellsManagement";
import ChlorineConfigManager from "./pages/ChlorineConfigManager";
import UsersManagement from "./pages/UsersManagement";
import Reports from "./pages/Reports";
import Profile from "./pages/Profile";
import CustomFieldsManagement from "./pages/CustomFieldsManagement";
import CSVImport from "./pages/CSVImport";
import ImportHistory from "./pages/ImportHistory";
import SystemHealth from "./pages/SystemHealth";
import DataExport from "./pages/DataExport";
import QRCodeManagement from "./pages/QRCodeManagement";
import QRDeepLink from "./pages/QRDeepLink";
import Unauthorized from "./pages/Unauthorized";
import SCADADemo from "./pages/SCADADemo";
import ArchiveExport from "./pages/ArchiveExport";
import RetentionSettings from "./pages/RetentionSettings";
import WastewaterModule from "./pages/WastewaterModule";
import TrafficModule from "./pages/TrafficModule";
import Login from "./pages/Login";
import InviteAccept from "./pages/InviteAccept";
import AdminInvites from "./pages/AdminInvites";
import AdminUsers from "./pages/AdminUsers";
import AdminStatus from "./pages/AdminStatus";
import AdminSystemLogs from "./pages/AdminSystemLogs";
import SystemStatus from "./pages/SystemStatus";
import AdminHealth from "./pages/AdminHealth";
import SupervisorDataViewer from "./pages/SupervisorDataViewer";
import AllWellsProduction from "./pages/AllWellsProduction";
import SupervisorStats from "./pages/SupervisorStats";
import FieldGuide from "./pages/FieldGuide";
import Help from "./pages/Help";

function Router() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/invite/:token" component={InviteAccept} />
      {/* Protected routes - redirect to /login if not authenticated */}
      <ProtectedRoute>
        <RoleProtectedRoute minRole="admin">
          <Route path="/admin/users" component={AdminUsers} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="admin">
          <Route path="/admin/invites" component={AdminInvites} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="admin">
          <Route path="/admin/status" component={AdminStatus} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="admin">
          <Route path="/admin/logs" component={AdminSystemLogs} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="admin">
          <Route path="/admin/health" component={AdminHealth} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="operator">
          <Route path="/" component={Home} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/wastewater" component={WastewaterModule} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/traffic" component={TrafficModule} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="operator">
          <Route path="/qr/:wellId" component={QRDeepLink} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="operator">
          <Route path="/logs/edit/:id">
            {({ id }) => <Redirect to={`/logs/${id}/edit`} />}
          </Route>
          <Route path="/logs/new" component={DailyLogEntry} />
        </RoleProtectedRoute>

        <RoleProtectedRoute minRole="operator">
          <Route path="/logs/:id/edit" component={DailyLogEntryWrapper} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="operator">
          <Route path="/logs" component={DailyLogList} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/wells" component={WellsManagement} />
          <Route path="/wells/:wellId/chlorine-config" component={ChlorineConfigManager} />
        </RoleProtectedRoute>

        <RoleProtectedRoute minRole="supervisor">
          <Route path="/users" component={UsersManagement} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/custom-fields" component={CustomFieldsManagement} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/csv-import" component={CSVImport} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/import-history" component={ImportHistory} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/system/status" component={SystemStatus} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/system-health" component={SystemHealth} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/data-export" component={DataExport} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/qr-codes" component={QRCodeManagement} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/archive-export" component={ArchiveExport} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="supervisor">
          <Route path="/retention-settings" component={RetentionSettings} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="operator">
          <Route path="/data-viewer" component={SupervisorDataViewer} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="operator">
          <Route path="/reports" component={Reports} />
        </RoleProtectedRoute>
        <RoleProtectedRoute minRole="operator">
          <Route path="/reports/all-wells-production" component={AllWellsProduction} />
        </RoleProtectedRoute>

        <RoleProtectedRoute minRole="supervisor">
          <Route path="/supervisor/stats" component={SupervisorStats} />
        </RoleProtectedRoute>

        <RoleProtectedRoute minRole="operator">
          <Route path="/profile" component={Profile} />
        </RoleProtectedRoute>

        <RoleProtectedRoute minRole="operator">
          <Route path="/help" component={Help} />
        </RoleProtectedRoute>

        <RoleProtectedRoute minRole="operator">
          <Route path="/help/field-guide" component={FieldGuide} />
        </RoleProtectedRoute>
      </ProtectedRoute>
      {/* Unprotected routes */}
          <Route path="/unauthorized" component={Unauthorized} />
          <Route path="/scada-demo" component={SCADADemo} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark" switchable={true}>
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
